import React, {
  memo,
  forwardRef,
  ChangeEvent,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import {
  standardCheckboxStyle,
  standardInputStyle,
  headerInputStyle,
  standardDescriptionStyle,
} from './styles/StandardInput.styles'

// Types for the checkbox component
interface StandardCheckboxProps {
  checked?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  color?: string | null
  size?: 'small' | 'medium' | 'big'
  id?: string
  'data-testid'?: string
}

// Standard Checkbox Component with controlled state
export const StandardCheckbox = forwardRef<
  HTMLInputElement,
  StandardCheckboxProps
>(
  (
    {
      checked = false,
      onChange,
      disabled = false,
      color = null,
      size = 'medium',
      id,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    return (
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        id={id}
        data-testid={dataTestId}
        css={[
          standardCheckboxStyle(size, color || undefined),
          disabled && {
            cursor: 'not-allowed',
            opacity: 0.5,
          },
        ]}
      />
    )
  },
)

// Types for the standard input component
interface StandardInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  value?: string
  type?: string
  disabled?: boolean
  'data-testid'?: string
}

// Standard Input Component with consistent styling across the application
export const StandardInput = memo(
  forwardRef<HTMLInputElement, StandardInputProps>(
    (
      {
        onChange,
        value,
        type = 'text',
        disabled = false,
        'data-testid': dataTestId,
        ...props
      },
      ref,
    ) => {
      const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        if (onChange) onChange(e)
      }

      return (
        <input
          ref={ref}
          type={type}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          data-testid={dataTestId}
          css={standardInputStyle}
          {...props}
        />
      )
    },
  ),
)

// Types for the description component
interface StandardDescriptionProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  onChange?: (value: string) => void
  value?: string
  disabled?: boolean
  rows?: number
  placeholder?: string
  'data-testid'?: string
}

// Description Component
export const StandardDescription = memo(
  forwardRef<HTMLTextAreaElement, StandardDescriptionProps>(
    (
      {
        onChange,
        value,
        disabled = false,
        rows = 4,
        placeholder = 'Enter description...',
        'data-testid': dataTestId,
        ...props
      },
      ref,
    ) => {
      const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
        if (onChange) onChange(e.target.value)
      }

      return (
        <textarea
          ref={ref}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          data-testid={dataTestId}
          css={standardDescriptionStyle}
          {...props}
        />
      )
    },
  ),
)

// Types for the header input component
interface HeaderInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  value?: string
  disabled?: boolean
  'data-testid'?: string
}

// Header Input Component
export const HeaderInput = memo(
  forwardRef<HTMLInputElement, HeaderInputProps>(
    (
      {
        onChange,
        value,
        disabled = false,
        'data-testid': dataTestId,
        ...props
      },
      ref,
    ) => {
      return (
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={onChange}
          disabled={disabled}
          data-testid={dataTestId}
          css={headerInputStyle}
          {...props}
        />
      )
    },
  ),
)
